import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { OidcSskService } from '@/api/generated';
import { SskFailureScreen } from '@/components/ssk-failure-screen';
import { SskSigningScreen } from '@/components/ssk-signing-screen';
import { FeatureFlag } from '@/enums/feature-flag';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { canUseSskBridge, requestBridgeSigning } from '@/utils/bridge-signing';
import { requestExtensionSigning } from '@/utils/extension-signing';
import type { OidcSskCompletionData } from '@/utils/oidc-ssk-utils';
import { makeFullPath } from '@/utils/path-utils';
import {
  SSK_FAILURE_COPY,
  SSK_REFERENCE_CODE,
  SSK_SSO_TYPE,
  type SskFailureBucket,
  reportSskFailure,
  supportMailto,
  toSskFailureBucket,
} from '@/utils/ssk-failure';
import { generateUUID } from '@/utils/tab-id';

type SigningPhase = 'waiting' | 'submitting' | 'redirecting' | 'error';

// Demo second share: base64 of a single 0x01 byte (sigma2 = 1). The server
// combines sigma1*1 = sigma1, so the RP receives a sigma1-only token it rejects.
const DEMO_NOOP_SIGMA2 = 'AQ==';

// No in-browser combine: post sigma2 and let the server combine it (store-partial).
export default function OidcSskCompleteSigningPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<SigningPhase>('waiting');
  const [failure, setFailure] = useState<{
    bucket: SskFailureBucket;
    referenceId: string;
    failedAt: Date;
    username?: string;
  } | null>(null);
  const inFlight = useRef(false);

  const forwardPartialOnFailure = useFeatureFlag(
    FeatureFlag.SPLIT_SAML_DEMO_PARTIAL_SIGNATURE
  );
  // Read at failure time, not mount time: the gate resolves async after mount.
  const forwardPartialRef = useRef(forwardPartialOnFailure);
  useEffect(() => {
    forwardPartialRef.current = forwardPartialOnFailure;
  }, [forwardPartialOnFailure]);

  const state = location.state as OidcSskCompletionData | null;
  const signingHandoff = state?.code && state?.digestHex ? state : null;

  useEffect(() => {
    if (signingHandoff) {
      void completeSigning(signingHandoff);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  async function completeSigning(data: OidcSskCompletionData) {
    if (inFlight.current) {
      return;
    }

    inFlight.current = true;

    try {
      // Only device-signing failures forward a partial; a POST failure is real.
      let sigma2: string;

      try {
        setPhase('waiting');
        const secondSig = canUseSskBridge()
          ? await requestBridgeSigning(data.digestHex)
          : await requestExtensionSigning(data.digestHex);
        sigma2 = btoa(String.fromCharCode(...secondSig.sigma2Bytes));
      } catch (signingError) {
        console.error('Split OIDC signing failed:', signingError);

        if (!forwardPartialRef.current) {
          throw signingError;
        }

        // Demo: no-op second share → server issues a sigma1-only token RP rejects.
        sigma2 = DEMO_NOOP_SIGMA2;
      }

      setPhase('submitting');
      const { redirect_url } = await OidcSskService.completeSigning({
        code: data.code,
        sigma2,
      });

      setPhase('redirecting');
      window.location.assign(redirect_url);
    } catch (error) {
      const bucket = toSskFailureBucket(error);
      const referenceId = generateUUID();
      setPhase('error');
      setFailure({ bucket, referenceId, failedAt: new Date() });
      // Not awaited: the report must not hold the signing lock or block Try again.
      void reportSskFailure({
        ssoType: SSK_SSO_TYPE.SPLIT_OIDC,
        appId: data.appId,
        bucket,
        error,
        referenceId,
      }).then(username =>
        setFailure(current =>
          current?.referenceId === referenceId
            ? { ...current, username }
            : current
        )
      );
    } finally {
      inFlight.current = false;
    }
  }

  function handleRetry() {
    if (signingHandoff) {
      setFailure(null);
      void completeSigning(signingHandoff);
    }
  }

  if (!signingHandoff) {
    return <Navigate to={makeFullPath('/auth-error')} replace />;
  }

  const waitingMessage = canUseSskBridge()
    ? 'Waiting for the mobile authenticator to secure connection...'
    : 'Waiting for browser extension to secure connection...';

  if (phase === 'error' && failure) {
    return (
      <SskFailureScreen
        copy={SSK_FAILURE_COPY[failure.bucket]}
        username={failure.username}
        supportMailto={supportMailto({
          supportEmail: signingHandoff.supportEmail,
          appName: signingHandoff.appName,
          username: failure.username,
          referenceId: failure.referenceId,
          failedAt: failure.failedAt,
        })}
        referenceCode={SSK_REFERENCE_CODE[failure.bucket]}
        onRetry={handleRetry}
        onStartOver={() => navigate(makeFullPath('/login'))}
      />
    );
  }

  return (
    <SskSigningScreen
      message={
        phase === 'waiting'
          ? waitingMessage
          : 'Signing into application using SSK...'
      }
      redirecting={phase === 'redirecting'}
    />
  );
}
